import { Header, StyledContainer } from '@/constants/themes';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Paper, styled, Switch, Typography } from '@mui/material';

import ContextVisualizer from '@/components/Context/visualizer';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { createTimeoutSignal } from '@/utils/signalConfig';
import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
  inputProps: { accept: '.csv,.docx,.doc,.pdf,.txt' },
});

// Define document processing chunk interface for streaming updates
interface DocumentProcessingChunk {
  type: 'progress' | 'complete' | 'error';
  value: number;
  message?: string;
  documentId?: string;
}

// // Mock function to simulate a stream of document processing progress Todo: Replace with actual API call
// async function* mockDocumentProcessingStream(
//   documentId: string
// ): AsyncGenerator<DocumentProcessingChunk> {
//   const progressSteps = [0, 20, 40, 60, 80, 100];

//   for (const progress of progressSteps) {
//     // Simulate network delay
//     await new Promise((resolve) => setTimeout(resolve, 1000));

//     if (progress < 100) {
//       yield {
//         type: 'progress',
//         value: progress,
//         message: `Processing document (${progress}%)`,
//         documentId,
//       };
//     } else {
//       yield {
//         type: 'complete',
//         value: 100,
//         message: 'Document processed successfully',
//         documentId,
//       };
//     }
//   }
// }

const DropzoneContainer = styled(Paper)(({ theme }) => ({
  border: '2px dashed #ccc',
  borderRadius: '8px',
  padding: '20px',
  textAlign: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  marginBottom: '20px',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '&.dragover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
  },
  '&.disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
    borderColor: '#ddd',
    '&:hover': {
      borderColor: '#ddd',
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
  },
}));

function ContextPage(): React.ReactNode {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [documentType, setDocumentType] = useState<'internal' | 'external'>(
    'internal'
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { snackbarEntries, setStoreState } = useAlchemystStoreForAi(
    (store) => ({
      snackbarEntries: store.snackbarEntries,
      setStoreState: store.setStoreState,
    })
  );
  const [hostName, setHostName] = useState('http://localhost:3000');

  const addSnackbarEntry = (entry: (typeof snackbarEntries)[0]) => {
    setStoreState({
      snackbarEntries: [...snackbarEntries, entry],
    });
  };

  /**
   * Processes streaming updates for document processing progress
   * @param notificationId The notification ID to update
   * @param documentId The document ID being processed
   */
  const handleDocumentProcessingStream = async (
    notificationId: string,
    documentId: string
  ) => {
    try {
      // In production, this would be a real stream from your API
      // const stream = fetchDocumentProcessingUpdates(documentId);
      // const stream = mockDocumentProcessingStream(documentId);
      // for await (const chunk of stream) {
      //   console.log('Received document processing update:', chunk);
      //   // Update the notification with the current progress
      //   const currentNotifications =
      //     useAlchemystStoreForAi.getState().snackbarEntries;
      //   // Find the notification to update
      //   const notificationExists = currentNotifications.some(
      //     (entry) => entry.notificationId === notificationId
      //   );
      //   if (!notificationExists) {
      //     console.log('Notification no longer exists, stopping updates');
      //     break;
      //   }
      //   // Update the notification based on the chunk type
      //   switch (chunk.type) {
      //     case 'progress':
      //       setStoreState({
      //         snackbarEntries: currentNotifications.map((entry) =>
      //           entry.notificationId === notificationId
      //             ? {
      //                 ...entry,
      //                 value: chunk.value,
      //                 message:
      //                   chunk.message ||
      //                   `Processing document (${chunk.value}%)`,
      //                 variant: 'determinate',
      //                 severity: 'info',
      //               }
      //             : entry
      //         ),
      //       });
      //       break;
      //     case 'complete':
      //       setStoreState({
      //         snackbarEntries: currentNotifications.map((entry) =>
      //           entry.notificationId === notificationId
      //             ? {
      //                 ...entry,
      //                 value: 100,
      //                 message:
      //                   chunk.message || 'Document processed successfully',
      //                 variant: 'determinate',
      //                 severity: 'success',
      //               }
      //             : entry
      //         ),
      //       });
      //       break;
      //     case 'error':
      //       setStoreState({
      //         snackbarEntries: currentNotifications.map((entry) =>
      //           entry.notificationId === notificationId
      //             ? {
      //                 ...entry,
      //                 message: chunk.message || 'Error processing document',
      //                 severity: 'error',
      //               }
      //             : entry
      //         ),
      //       });
      //       break;
      //   }
      // }
    } catch (error) {
      console.error('Error handling document processing stream:', error);

      // Update notification with error state
      const currentNotifications =
        useAlchemystStoreForAi.getState().snackbarEntries;
      setStoreState({
        snackbarEntries: currentNotifications.map((entry) =>
          entry.notificationId === notificationId
            ? {
                ...entry,
                message: 'Error processing document',
                severity: 'error',
              }
            : entry
        ),
      });
    }
  };

  useEffect(() => {
    if (!!window) {
      setHostName(window.location.origin);
    }
  }, []);

  const handleSingleFileUpload = async (
    file: File | null,
    scope: 'internal' | 'external',
    ttlFiles: number,
    index: number
  ) => {
    try {
      const MAX_SIZE = 50 * 1024 * 1024; // 50 MB
      if (!file) return;
      if (file.size > MAX_SIZE) {
        addSnackbarEntry({
          message: 'Uploaded file must be under 50MB',
          severity: 'error',
          type: 'alert',
          notificationId: crypto.randomUUID(),
        });
        return;
      }
      setIsUploading(true);

      // Prepare file metadata
      const fileMetadata = {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        lastModified: file.lastModified,
      };

      const notificationId = `platform.resource.${fileMetadata.fileName}.${fileMetadata.fileType}`;
      const documentId = `doc_${Date.now()}`; // In production, this would come from the API

      try {
        const contextProcessorUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/context/add`;
        if (!contextProcessorUrl) {
          throw new Error('CONTEXT_PROCESSOR_URL is not defined');
        }

        const fileContent = await readFileContent(file);
        console.log(
          'File content = ',
          fileContent.slice(Math.min(60, fileContent.length))
        );

        const fetchUserIdResponse = await fetchWithRewrites('/api/v1/profile', {
          signal: createTimeoutSignal(),
        });
        const fetchUserIdJson: { user_id: string } =
          await fetchUserIdResponse.json();
        const userId = fetchUserIdJson.user_id;
        console.log('Received userId = ', userId);

        // Start processing the document with streaming progress updates
        // handleDocumentProcessingStream(notificationId, documentId);

        // console.log('File reading complete, posting message to worker...');
        // await worker.postMessage({ fileContent, apiUrl, fileMetadata, userId });
        // worker.postMessage({
        //   fileContent,
        //   contextProcessorUrl,
        //   fileMetadata,
        //   userId,
        //   accessToken,
        // });

        const documentData = {
          content: fileContent,
          ...fileMetadata,
        };

        const uploadDocumentsRes = await fetchWithRewrites(
          contextProcessorUrl,
          {
            method: 'POST',
            body: JSON.stringify({
              // user_id: userId,
              documents: [documentData],
              source: 'platform/maya/smart-settings.upload',
              context_type: 'resource',
              chained: false,
              scope,
              metadata: {
                fileSize: file.size,
                fileName: file.name,
                fileType: file.type,
                lastModified: new Date(file.lastModified).toISOString(),
                modalities: ['text', 'image'],
              },
            }),
            signal: createTimeoutSignal(),
          }
        );

        const documentUploadResult = await uploadDocumentsRes.json();

        if (uploadDocumentsRes.ok) {
          // console.log('Document processed successfully:', documentUploadResult);
          // setStoreState({
          //   snackbarEntries: [
          //     ...snackbarEntries,
          //     {
          //       notificationId,
          //       type: 'progress',
          //       value: 100,
          //       message: 'Documents processed successfully',
          //       severity: 'success',
          //     },
          //   ],
          // });

          setStoreState({
            snackbarEntries: [
              ...snackbarEntries,
              {
                type: 'progress',
                value: 100,
                message: `Document ${file.name} processed successfully`,
                severity: 'success',
                notificationId,
                variant: 'determinate',
              },
            ],
          });
        }
      } catch (error) {
        console.error('Error processing file:', error);
      } finally {
        setIsUploading(false);
      }
    } catch (error) {
      console.error('Error processing file:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files: FileList) => {
    const totalFiles = files.length;
    setTotalFiles(totalFiles);

    setStoreState({
      snackbarEntries: [
        ...snackbarEntries,
        {
          type: 'progress',
          value: 0,
          message: `Uploading 0/${totalFiles} documents`,
          severity: 'info',
          notificationId: crypto.randomUUID(),
          variant: 'indeterminate',
        },
      ],
    });

    // Process files
    (async () => {
      for (const [index, file] of [...files].entries()) {
        setUploadedFiles(index + 1);
        setStoreState({
          snackbarEntries: [
            ...snackbarEntries,
            {
              type: 'progress',
              value: Math.round((index / totalFiles) * 100),
              message: `Uploading ${index + 1}/${totalFiles} documents`,
              severity: 'info',
              notificationId: crypto.randomUUID(),
              variant: 'determinate',
            },
          ],
        });

        await handleSingleFileUpload(file, documentType, totalFiles, index);
      }

      setUploadedFiles(0);
      setTotalFiles(0);
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            type: 'alert',
            message: 'All Files have been uploaded',
            severity: 'info',
            notificationId: crypto.randomUUID(),
          },
        ],
      });
    })();
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const readFileContent = async (file: File): Promise<string> => {
    console.log('Reading file...');
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    switch (fileExtension) {
      case 'pdf':
        return readPdfContent(file);
      case 'docx':
        return readDocxContent(file);
      case 'xlsx':
        return JSON.stringify(await readXlsxContent(file));
      default:
        console.log('Reading text file...');
        return file.text();
    }
  };

  const readPdfContent = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    console.log('Getting PDF text...');
    try {
      const response = await fetchWithRewrites('/api/v1/upload', {
        method: 'POST',
        body: formData,
        signal: createTimeoutSignal(),
      });

      if (!response.ok) {
        console.log('Failed to process PDF');
      }

      const responseData: Record<string, any> & { text: string } =
        await response.json();

      console.log('Finished uploading pdf file to context processor.');

      return responseData.text;
    } catch (error) {
      console.error('Error processing PDF:', error);
      throw error;
    }
  };

  const readDocxContent = async (file: File): Promise<string> => {
    console.log('Reading Docx File...');
    // This function would use the mammoth package to read DOCX content
    // For demonstration, we'll use a placeholder implementation
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve('DOCX content placeholder');
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const readXlsxContent = async (file: File): Promise<string[][]> => {
    console.log('Reading XLSX File...');

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;

        if (result) {
          try {
            const data = new Uint8Array(result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            console.log(jsonData);
            // @ts-ignore
            resolve(jsonData);
          } catch (error) {
            console.error('Error parsing XLSX file:', error);
            reject(error);
          }
        } else {
          reject(new Error('Failed to read file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('File reading failed'));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const storeFileContentInIndexedDB = async (
    fileName: string,
    content: string
  ) => {
    console.log('Writing to IndexedDB...');
    const dbName = 'FileContentsDB';
    const storeName = 'fileContents';
    const version = 1;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, version);

      request.onerror = (event) => reject('IndexedDB error');

      request.onsuccess = (event) => {
        const db = request.result;
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const addRequest = store.put({ fileName, content });

        addRequest.onerror = () => reject('Error storing file content');
        addRequest.onsuccess = () => resolve(undefined);
      };

      request.onupgradeneeded = (event) => {
        const db = request.result;
        db.createObjectStore(storeName, { keyPath: 'fileName' });
      };
    });
  };

  return (
    <StyledContainer maxWidth="xl">
      <Header>
        <Box
          display="flex"
          alignItems="center"
          justifyContent={'space-between'}
          width={'100%'}
          gap={2}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              Context
            </Typography>
            <Typography variant="body1" marginTop={2}>
              Manage your organizational context here.
            </Typography>
          </Box>
        </Box>
      </Header>

      <DropzoneContainer
        className={`${isDragOver ? 'dragover' : ''} ${isUploading ? 'disabled' : ''}`}
        onDragOver={isUploading ? undefined : handleDragOver}
        onDragLeave={isUploading ? undefined : handleDragLeave}
        onDrop={isUploading ? undefined : handleDrop}
        onClick={isUploading ? undefined : openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={isUploading}
        />
        <CloudUploadIcon
          sx={{
            fontSize: 48,
            color: isUploading ? 'text.disabled' : 'primary.main',
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          gutterBottom
          color={isUploading ? 'text.disabled' : 'text.primary'}
        >
          {isUploading ? 'Uploading...' : 'Drag & Drop Files Here'}
        </Typography>
        <Typography
          variant="body2"
          color={isUploading ? 'text.disabled' : 'text.secondary'}
        >
          {isUploading
            ? 'Please wait until upload completes'
            : 'Or click to browse (Max 50 MB per file)'}
        </Typography>
      </DropzoneContainer>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Box display="flex" flexDirection="row" gap={2} alignItems="center">
          <Typography>
            Allow these documents to be used in public-facing tasks?
          </Typography>
          <Switch
            checked={documentType === 'external'}
            onChange={() => {
              setDocumentType(
                documentType === 'internal' ? 'external' : 'internal'
              );
            }}
            disabled={isUploading}
          />
        </Box>
      </Box>

      <ContextVisualizer />
    </StyledContainer>
  );
}

export default ContextPage;
